import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Target, ArrowRight, TrendUp, TrendDown, Sparkle } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * Broker Coaching Card — Dashboard surface for the AI Negotiator's coaching insights.
 *
 * Same data the Monday Weekly Digest uses (`_gather_broker_coaching`), but
 * available every time the pilot opens the app. Emerald tint = under-floor
 * (missed revenue reclaim). Amber tint = over-ceiling (wasted bids).
 *
 * Auto-hides on well-calibrated pilots and fresh accounts.
 */
export default function BrokerCoachingCard() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    client.get("/pilot-digest/coaching-preview")
      .then((r) => alive && setRows(r.data.coaching || []))
      .catch((e) => alive && toast.error(e?.response?.data?.detail || "Coaching preview failed"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-6" data-testid="coaching-card-loading">
        <div className="animate-pulse text-neutral-500 text-sm">Loading broker coaching…</div>
      </div>
    );
  }
  if (!rows || rows.length === 0) return null;

  const totalImpact = rows.reduce((sum, r) => sum + Math.abs(r.est_impact_per_bid_usd || 0), 0);

  return (
    <div className="card-surface p-6" data-testid="coaching-card">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] mb-1">
            <Target size={13} weight="bold" />
            AI NEGOTIATOR COACHING
          </div>
          <h3 className="font-display text-lg font-semibold text-white">
            Your recent asks are outside the learned bracket
          </h3>
          <p className="text-[11px] text-neutral-500 mt-1 max-w-md">
            Recent-30d asks vs. the AI's learned floor + ceiling per broker.{" "}
            Same math the <span className="text-indigo-300">Draft Bid modal</span> uses on your next bid.
          </p>
        </div>
        <div className="inline-flex items-center gap-1.5 text-[11px] rounded-full border border-[#FF5A1F]/30 bg-[#FF5A1F]/10 text-[#FF5A1F] px-3 py-1 shrink-0">
          <Sparkle size={11} weight="fill" />
          ~${Math.round(totalImpact).toLocaleString()}/bid at stake
        </div>
      </div>

      <div className="space-y-3" data-testid="coaching-rows">
        {rows.map((r) => (
          <CoachingRow key={r.broker + r.type} row={r} />
        ))}
      </div>

      <div className="mt-4 flex justify-end">
        <Link
          to="/app/loadboard/analytics"
          data-testid="coaching-view-analytics"
          className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-[#FF5A1F] transition-colors"
        >
          See full broker brackets <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
    </div>
  );
}


function CoachingRow({ row }) {
  const isUnderFloor = row.type === "under_floor";
  const tone = isUnderFloor
    ? "border-emerald-500/30 bg-emerald-500/[0.06]"
    : "border-amber-500/30 bg-amber-500/[0.06]";
  const IconEl = isUnderFloor ? TrendUp : TrendDown;
  const iconTone = isUnderFloor ? "text-emerald-300" : "text-amber-300";
  const headlineTone = isUnderFloor ? "text-emerald-200" : "text-amber-200";
  const headlineText = isUnderFloor
    ? "You've been asking too low"
    : "You've been asking too high";
  const impactTone = isUnderFloor ? "text-emerald-300" : "text-amber-300";
  const impactLabel = isUnderFloor
    ? "left on the table"
    : "saved on bids you'd otherwise lose";

  const rowTestId = `coaching-row-${row.broker.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className={`rounded-lg border p-4 ${tone}`} data-testid={rowTestId}>
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <IconEl size={13} weight="bold" className={iconTone} />
            <div className="text-sm font-semibold text-white truncate">{row.broker}</div>
          </div>
          <div className={`text-[11px] mt-0.5 uppercase tracking-wider font-medium ${headlineTone}`}>
            {headlineText}
          </div>
        </div>
        <div className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold tabular-nums ${
          isUnderFloor ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                       : "border-amber-500/40 bg-amber-500/15 text-amber-300"
        }`}>
          {row.gap_pct}% {isUnderFloor ? "below floor" : "above ceiling"}
        </div>
      </div>

      {/* Coaching narrative */}
      <div className="text-xs text-neutral-300 leading-relaxed mt-2">
        Your last <b>{row.recent_asks_count} asks</b> avg{" "}
        <b className="tabular-nums">${Number(row.recent_avg_ask_per_mile).toFixed(2)}/mi</b>
        {isUnderFloor && row.floor_per_mile != null && (
          <>. Their <span className="text-emerald-300">proven floor</span> is{" "}
            <b className="tabular-nums">${Number(row.floor_per_mile).toFixed(2)}/mi</b></>
        )}
        {!isUnderFloor && row.ceiling_per_mile != null && (
          <>. Their <span className="text-rose-300">rejection ceiling</span> is{" "}
            <b className="tabular-nums">${Number(row.ceiling_per_mile).toFixed(2)}/mi</b></>
        )}
        .
      </div>

      {/* Suggested action */}
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-neutral-400">
          Try anchoring at{" "}
          <b className={`tabular-nums ${isUnderFloor ? "text-emerald-300" : "text-amber-300"}`}>
            ~${Number(row.suggested_anchor_per_mile).toFixed(2)}/mi
          </b>
        </div>
        <div className={`text-xs tabular-nums ${impactTone}`}>
          <b>+${Math.round(row.est_impact_per_bid_usd || 0).toLocaleString()}</b>/bid {impactLabel}
        </div>
      </div>
    </div>
  );
}
